import React, { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import Loader from "../../../shared/components/Loader";
import ActionToast from "../../../shared/components/ActionToast";
import colors from "../../../shared/themes/colors";
import { passwordRegex } from "../../../shared/validators";
import { changePassword, clearError, clearSuccess, setOpenSnackbar, toggleOldPasswordVisibility, toggleNewPasswordVisibility, toggleConfirmPasswordVisibility } from "../customerAccountSlice";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import InputAdornment from "@mui/material/InputAdornment";
import MachineLogoIcon from "../../../shared/assets/icons/MachineLogoIcon";
import { ChangeAccountPasswordForm } from "../../shared/models/customerModels";
import useTranslation from "../../../shared/hooks/useTranslation";

interface ChangeAccountPasswordDialogueProps {
  open: boolean;
  onClose: () => void;
}

const ChangeAccountPasswordDialogue = ({ open, onClose }: ChangeAccountPasswordDialogueProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate: tAccount } = useTranslation("account");
  const { translate: tCommon } = useTranslation("common");

  const {
    isSaving,
    success,
    openSnackbar,
    alertMessage,
    severity,
    showOldPassword,
    showNewPassword,
    showConfirmPassword,
  } = useAppSelector((state) => state.customerAccount);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ChangeAccountPasswordForm>({
    mode: "onBlur"
  });

  const handleClickShowOldPassword = () => dispatch(toggleOldPasswordVisibility());
  const handleClickShowNewPassword = () => dispatch(toggleNewPasswordVisibility());
  const handleClickShowConfirmPassword = () => dispatch(toggleConfirmPasswordVisibility());
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const validateConfirmPassword = (value?: string) => {
    const newPassword = watch("newPassword");
    if (!value || value.trim() === "" || !newPassword || newPassword.trim() === "") {
      return true;
    }
    return newPassword === value || tAccount("passwordsDoNotMatch");
  };

  const disableSubmit = watch("oldPassword") === "" || watch("newPassword") === "" || watch("confirmPassword") === "";

  useEffect(() => {
    if (!open) {
      reset();
      dispatch(clearError());
      dispatch(clearSuccess());
    }
  }, [open, dispatch, reset]);

  useEffect(() => {
    if (success) {
      onClose();
      dispatch(clearSuccess());
    }
  }, [success, onClose, dispatch]);

  const onSubmit = (data: ChangeAccountPasswordForm, event?: React.BaseSyntheticEvent) => {
    if (event) {
      event.stopPropagation();
    }
    dispatch(changePassword({ oldPassword: data.oldPassword, newPassword: data.newPassword }));
  };

  return (
    <>
      <ActionToast
        open={openSnackbar}
        variant={severity}
        message={tAccount(alertMessage || "passwordChangeFailed")}
        onClose={() => dispatch(setOpenSnackbar(false))}
        duration={6000}
      />
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth={false}
        hideBackdrop={isMobile}
        PaperProps={{
          sx: {
            ...(isMobile ? {
              margin: 0,
              borderRadius: "8px",
              maxHeight: "100vh",
              height: "100vh",
              width: "100vw",
              minWidth: "320px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              padding: "16px",
            } : {
              width: "600px",
              minHeight: "406px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 64px 64px",
              borderRadius: "8px",
            })
          }
        }}
      >
        {isSaving ? (
          <Box
            sx={{
              ...(isMobile ? {
                width: '100%',
                height: '100vh',
              } : {
                minWidth: "400px",
                minHeight: "406px",
              }),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Loader />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: 'flex',
              flexDirection: 'column',
              ...(isMobile && {
                justifyContent: 'space-between'
              })
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                mt: isMobile ? "60px" : "64px",
                ...(isMobile && {
                  flex: "0 0 auto"
                })
              }}
            >
              <MachineLogoIcon />
              <Box
                sx={{
                  fontFamily: "Work Sans",
                  fontSize: isMobile ? "32px" : "36px",
                  fontWeight: "500",
                  lineHeight: isMobile ? "38px" : "42px",
                  letterSpacing: "-0.072px",
                  ...(isMobile && {
                    marginTop: "16px"
                  })
                }}
              >
                {tAccount("changePassword")}
              </Box>
            </DialogTitle>

            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: "absolute",
                right: isMobile ? 16 : 14,
                top: isMobile ? 16 : 14,
                color: colors.secondary.gray20,
                ...(isMobile && {
                  padding: "12px",
                  '& .MuiSvgIcon-root': {
                    fontSize: '24px'
                  }
                })
              }}
            >
              <CloseIcon />
            </IconButton>

            <DialogContent
              sx={{
                width: "100%",
                padding: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                ...(isMobile && {
                  flex: "1 1 auto",
                  justifyContent: "center",
                  padding: "18px 8px"
                })
              }}
            >
              <Box
                component="form"
                id="changePasswordForm"
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                  mt: "36px"
                }}
              >
                <Box sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px"
                }}>
                  <InputLabel>{tAccount("currentPassword")}</InputLabel>
                  <TextField
                    fullWidth
                    type={showOldPassword ? "text" : "password"}
                    sx={{ width: "100%" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowOldPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showOldPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register("oldPassword", { required: tAccount("fieldRequired") })}
                    error={!!errors.oldPassword}
                    helperText={errors.oldPassword ? errors.oldPassword.message || tAccount("fieldInvalid") : ""}
                  />
                </Box>
                <Box sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px"
                }}>
                  <InputLabel>{tAccount("newPassword")}</InputLabel>
                  <TextField
                    type={showNewPassword ? "text" : "password"}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowNewPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showNewPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register("newPassword", {
                      required: tAccount("fieldRequired"),
                      pattern: { value: passwordRegex, message: tAccount("passwordRequirements") },
                    })}
                    error={!!errors.newPassword}
                    helperText={errors.newPassword ? errors.newPassword.message || tAccount("fieldInvalid") : ""}
                  />
                </Box>
                <Box sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px"
                }}>
                  <InputLabel>{tAccount("confirmNewPassword")}</InputLabel>
                  <TextField
                    type={showConfirmPassword ? "text" : "password"}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowConfirmPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showConfirmPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register("confirmPassword", {
                      required: tAccount("fieldRequired"),
                      validate: validateConfirmPassword
                    })}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword ? errors.confirmPassword.message || tAccount("fieldInvalid") : ""}
                  />
                </Box>
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                width: "100%",
                display: "flex",
                gap: "12px",
                mt: 1,
                ...(isMobile ? {
                  flexDirection: "column",
                  padding: "16px 8px"
                } : {
                  flexDirection: "row",
                  padding: "16px 0"
                })
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                onClick={onClose}
                sx={{
                  ...(isMobile ? {
                    order: 2
                  } : {
                    order: 1
                  })
                }}
              >
                {tCommon("buttons.cancel")}
              </Button>
              <Button
                fullWidth
                form="changePasswordForm"
                type="submit"
                variant="contained"
                disabled={disableSubmit}
                sx={{
                  ...(isMobile ? {
                    order: 1
                  } : {
                    order: 2
                  })
                }}
              >
                {tAccount("changePassword")}
              </Button>
            </DialogActions>
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default ChangeAccountPasswordDialogue; 